import {
  capitalize,
  FormHelperText,
  Grid2,
  TextField,
  useTheme,
} from "@mui/material";
import { Box, useMediaQuery } from "@mui/system";
import {
  Initiative,
  InitiativeImpactSeries,
  MonthlyImpactMap,
  UpdateStatus,
} from "aint-types";
import {
  FLOAT_REGEX,
  FULL_YEAR_MONTHS,
  IMPACT_SERIES_IDS_AND_LABELS,
  LARGE_SCREEN_BREAKPOINT,
  MONTH_TO_NUMBER,
} from "aint-utils";
import dayjs, { Dayjs } from "dayjs";
import { useContext, useState } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";

import { DefaultValuesContext } from "../../app/pages/update-impact-dialog/update-impact-default-values-context";
import { getImpactFieldColors } from "../../app/utils";
import { getNestedPropValue } from "../../app/utils/get-nested-prop-value";
import { FORM_FIELD_INFO_TEXT } from "../../form/form-field-info-text";
import { EditDialogHeadline } from "../edit-dialog-headline/edit-dialog-headline";
import { INFormFieldInfoSlot } from "../form-fields/in-form-field-info-slot";
import { InfoSlot } from "../info-slot/info-slot";

type ImpactPlanSectionProps = {
  /** The year this impact plan section is for. */
  year: string;
  /** The months this impact plan section is for. */
  months: string[];
  /** The id of the impact series. */
  seriesId: string;
  /** The label of the impact series. */
  seriesLabel: string;
  /** Flag indicating if the section should disabled planned fields and show actual fields. */
  isReportingUpdate?: boolean;
  /** Should plan values be locked for example because of the PD Lockstamp. */
  lockPlanValues?: boolean;
  /** The breakpoint which causes the section to displays in two rows instead of one. */
  twoRowBreakpoint?: string;
  activeInitiativeUID: string;
  confirmationTypes?: Record<string, Record<string, UpdateStatus>>;
};

export function ImpactPlanSection({
  year,
  months,
  seriesId,
  confirmationTypes,
  isReportingUpdate = false,
  lockPlanValues = false,
  twoRowBreakpoint = LARGE_SCREEN_BREAKPOINT,
  activeInitiativeUID,
}: ImpactPlanSectionProps) {
  const isLargeScreen = useMediaQuery(twoRowBreakpoint);
  const theme = useTheme();
  const rows = isLargeScreen ? 1 : 2;
  const seriesLabel =
    IMPACT_SERIES_IDS_AND_LABELS.find((s) => s.id === seriesId)?.label ?? "";

  const {
    errors: { [seriesId + "Planned"]: error },
  } = useFormState();
  return (
    <Box
      sx={{
        marginTop: "8px",
        padding: "16px",
        border: `1px solid ${!!error ? theme.palette.error.main : "#D8D8D8"}`,
        borderRadius: "12px",
        fontSize: "12px",
        fontWeight: 700,
        marginBottom: "8px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <EditDialogHeadline>
            {isReportingUpdate
              ? seriesLabel +
                (seriesId.toLowerCase().includes("fte")
                  ? " (in # of FTE)"
                  : " (in k EUR)")
              : year}
          </EditDialogHeadline>
          {error && (
            <FormHelperText sx={{ color: theme.palette.error.main }}>
              {error.message as string}
            </FormHelperText>
          )}
        </Box>
        <InfoSlot>
          <INFormFieldInfoSlot
            text={
              FORM_FIELD_INFO_TEXT[
                (seriesId +
                  (isReportingUpdate
                    ? "Actual"
                    : "Planned")) as keyof typeof FORM_FIELD_INFO_TEXT
              ]
            }
          />
        </InfoSlot>
      </Box>

      <ImpactRows
        year={year}
        seriesId={seriesId}
        seriesLabel={seriesLabel}
        rows={rows}
        months={months}
        isReportingUpdate={isReportingUpdate}
        lockPlanValues={lockPlanValues}
        activeInitiativeUID={activeInitiativeUID}
        confirmationTypes={confirmationTypes}
      />
    </Box>
  );
}

function ImpactRows({
  year,
  seriesId,
  seriesLabel,
  rows,
  months,
  isReportingUpdate,
  lockPlanValues,
  activeInitiativeUID,
  confirmationTypes,
}: ImpactPlanSectionProps & { rows: 1 | 2 }) {
  const tieIncludesHrCosts = useWatch({
    name: "tieServiceExpensesIncludesHrCosts",
  });

  return (
    <>
      {seriesId === "tieServiceExpensesImpact" && tieIncludesHrCosts ? (
        <TieAndHrCostImpactSection
          year={year}
          seriesId={seriesId}
          seriesLabel={seriesLabel}
          months={months}
          isReportingUpdate={isReportingUpdate}
          lockPlanValues={lockPlanValues}
          activeInitiativeUID={activeInitiativeUID}
          confirmationTypes={confirmationTypes}
        />
      ) : (
        <ImpactSection
          year={year}
          seriesId={seriesId}
          seriesLabel={seriesLabel}
          rows={rows}
          months={months}
          isReportingUpdate={isReportingUpdate}
          lockPlanValues={lockPlanValues}
          activeInitiativeUID={activeInitiativeUID}
          confirmationTypes={confirmationTypes}
        />
      )}
    </>
  );
}

function ImpactSection({
  year,
  seriesId,
  rows,
  months,
  isReportingUpdate,
  lockPlanValues,
  activeInitiativeUID,
  confirmationTypes,
}: ImpactPlanSectionProps & { rows: 1 | 2 }) {
  const defaultValues = useContext(DefaultValuesContext) as Initiative;

  return (
    <>
      <Box>
        <Row
          year={year}
          isPlanned={true}
          seriesId={seriesId}
          fields={rows === 1 ? FULL_YEAR_MONTHS : FULL_YEAR_MONTHS.slice(0, 6)}
          months={months}
          rows={rows}
          fieldsDisabled={isReportingUpdate}
          lockValues={lockPlanValues}
          activeInitiativeUID={activeInitiativeUID}
          confirmationTypes={confirmationTypes}
        />
        {isReportingUpdate && (
          <Row
            year={year}
            isPlanned={false}
            seriesId={seriesId}
            fields={
              rows === 1 ? FULL_YEAR_MONTHS : FULL_YEAR_MONTHS.slice(0, 6)
            }
            activeInitiativeUID={activeInitiativeUID}
            months={months}
            rows={rows}
            confirmationTypes={confirmationTypes}
            seriesDefaultValues={
              defaultValues[
                (seriesId + "Actual") as keyof InitiativeImpactSeries
              ] as MonthlyImpactMap
            }
          />
        )}
      </Box>
      {rows === 2 && (
        <Box sx={{ marginTop: "12px" }}>
          <Row
            year={year}
            isPlanned={true}
            seriesId={seriesId}
            activeInitiativeUID={activeInitiativeUID}
            fields={FULL_YEAR_MONTHS.slice(6, 12)}
            months={months}
            rows={rows}
            fieldsDisabled={isReportingUpdate}
            confirmationTypes={confirmationTypes}
          />
          {isReportingUpdate && (
            <Row
              year={year}
              isPlanned={false}
              seriesId={seriesId}
              activeInitiativeUID={activeInitiativeUID}
              fields={FULL_YEAR_MONTHS.slice(6, 12)}
              months={months}
              rows={rows}
              confirmationTypes={confirmationTypes}
              seriesDefaultValues={
                defaultValues[
                  (seriesId + "Actual") as keyof InitiativeImpactSeries
                ] as MonthlyImpactMap
              }
            />
          )}
        </Box>
      )}
    </>
  );
}

/**
 * If the tieServiceExpensesIncludesHrCosts checkbox is checked, the tie service impact impact plan section is extended by the hr costs.
 * This duplicates some of the code of the standard impact section. While this increases maintenance effort, we wanted to avoid increasing the complexity
 * of the component even further.
 */
export function TieAndHrCostImpactSection({
  year,
  months,
  isReportingUpdate = false,
  lockPlanValues = false,
  twoRowBreakpoint = LARGE_SCREEN_BREAKPOINT,
  activeInitiativeUID,
  confirmationTypes,
}: ImpactPlanSectionProps) {
  const isLargeScreen = useMediaQuery(twoRowBreakpoint);
  const rows = isLargeScreen ? 1 : 2;
  const defaultValues = useContext(DefaultValuesContext) as Initiative;

  return (
    <>
      <Box>
        <Row
          year={year}
          isPlanned={true}
          seriesId="tieServiceExpensesImpact"
          fields={rows === 1 ? FULL_YEAR_MONTHS : FULL_YEAR_MONTHS.slice(0, 6)}
          months={months}
          rows={rows}
          fieldsDisabled={isReportingUpdate}
          lockValues={lockPlanValues}
          activeInitiativeUID={activeInitiativeUID}
          confirmationTypes={confirmationTypes}
        />
        <Row
          labelOverride="Planned t/o HR Expenses"
          year={year}
          isPlanned={true}
          seriesId="hrCostImpact"
          fields={rows === 1 ? FULL_YEAR_MONTHS : FULL_YEAR_MONTHS.slice(0, 6)}
          months={months}
          rows={rows}
          fieldsDisabled={isReportingUpdate}
          lockValues={lockPlanValues}
          activeInitiativeUID={activeInitiativeUID}
          confirmationTypes={confirmationTypes}
        />
        {isReportingUpdate && (
          <>
            <Row
              year={year}
              isPlanned={false}
              seriesId="tieServiceExpensesImpact"
              fields={
                rows === 1 ? FULL_YEAR_MONTHS : FULL_YEAR_MONTHS.slice(0, 6)
              }
              activeInitiativeUID={activeInitiativeUID}
              months={months}
              confirmationTypes={confirmationTypes}
              rows={rows}
              seriesDefaultValues={
                defaultValues.tieServiceExpensesImpactActual as MonthlyImpactMap
              }
            />
            <Row
              labelOverride="Actual / Forecast t/o HR Expenses"
              year={year}
              isPlanned={false}
              seriesId="hrCostImpact"
              fields={
                rows === 1 ? FULL_YEAR_MONTHS : FULL_YEAR_MONTHS.slice(0, 6)
              }
              activeInitiativeUID={activeInitiativeUID}
              months={months}
              confirmationTypes={confirmationTypes}
              rows={rows}
              seriesDefaultValues={
                defaultValues.hrCostImpactActual as MonthlyImpactMap
              }
            />
          </>
        )}
      </Box>
      {rows === 2 && (
        <Box sx={{ marginTop: "12px" }}>
          <Row
            year={year}
            isPlanned={true}
            seriesId="tieServiceExpensesImpact"
            activeInitiativeUID={activeInitiativeUID}
            fields={FULL_YEAR_MONTHS.slice(6, 12)}
            months={months}
            rows={rows}
            confirmationTypes={confirmationTypes}
            fieldsDisabled={isReportingUpdate}
          />
          <Row
            labelOverride="Planned t/o HR Expenses"
            year={year}
            isPlanned={true}
            seriesId="hrCostImpact"
            activeInitiativeUID={activeInitiativeUID}
            fields={FULL_YEAR_MONTHS.slice(6, 12)}
            months={months}
            rows={rows}
            fieldsDisabled={isReportingUpdate}
            confirmationTypes={confirmationTypes}
          />
          {isReportingUpdate && (
            <>
              <Row
                year={year}
                isPlanned={false}
                seriesId="tieServiceExpensesImpact"
                activeInitiativeUID={activeInitiativeUID}
                fields={FULL_YEAR_MONTHS.slice(6, 12)}
                months={months}
                rows={rows}
                confirmationTypes={confirmationTypes}
                seriesDefaultValues={
                  defaultValues.tieServiceExpensesImpactActual as MonthlyImpactMap
                }
              />
              <Row
                labelOverride="Actual / Forecast t/o HR Expenses"
                year={year}
                isPlanned={false}
                seriesId="hrCostImpact"
                activeInitiativeUID={activeInitiativeUID}
                fields={FULL_YEAR_MONTHS.slice(6, 12)}
                months={months}
                rows={rows}
                confirmationTypes={confirmationTypes}
                seriesDefaultValues={
                  defaultValues.hrCostImpactActual as MonthlyImpactMap
                }
              />
            </>
          )}
        </Box>
      )}
    </>
  );
}

function Row({
  year,
  seriesId,
  isPlanned,
  rows = 1,
  fields,
  months,
  fieldsDisabled = false,
  lockValues = false,
  labelOverride,
  confirmationTypes,
  seriesDefaultValues,
}: {
  /** The year this impact plan section is for. */
  year: string;
  /** The id of the impact series. */
  seriesId: string;
  isPlanned: boolean;
  fields: string[];
  /** The months this impact plan section is for. */
  months: string[];
  /** How many rows should the content be shown in. */
  rows: 1 | 2;
  /** Should the fields in the row(s) be disabled. */
  fieldsDisabled?: boolean;
  /** Should the fields of this row be disabled. */
  lockValues?: boolean;
  activeInitiativeUID: string;
  labelOverride?: string;
  confirmationTypes?: Record<string, Record<string, UpdateStatus>>;
  seriesDefaultValues?: MonthlyImpactMap;
}) {
  const fullSeriesId = seriesId + (isPlanned ? "Planned" : "Actual");

  return (
    <Grid2 container size={{ xs: 12 }} alignItems="flex-end">
      <Grid2
        container
        flexDirection="column"
        size={{ xs: rows }}
        justifyContent="flex-end"
      >
        <Box sx={{ marginBottom: rows === 1 ? "14px" : "8px" }}>
          {labelOverride
            ? labelOverride
            : isPlanned
              ? "Planned"
              : "Actual / Forecast"}
        </Box>
      </Grid2>
      <Grid2 container size={{ xs: 12 - rows }}>
        {(fields ?? []).map((field, index) => {
          const month = MONTH_TO_NUMBER[field];
          const dateIsDue = isDateDue(dayjs(`${year}-${month + 1}-1T12:00:00`));

          const disabled =
            lockValues ||
            fieldsDisabled ||
            !months.includes(field) ||
            (!isPlanned && dateIsDue && !!seriesDefaultValues?.[year]?.[field]);

          return (
            <Grid2
              container
              key={`${fullSeriesId}.${year}.${field}-impact-plan-section-${index}`}
              size={{ xs: rows }}
              flexDirection="column"
              alignItems="center"
              sx={{ padding: rows === 2 ? "4px" : "8px" }}
            >
              <span>{capitalize(field)}</span>
              <EditField
                disabled={disabled}
                confirmationType={confirmationTypes?.[year]?.[field]}
                name={`${fullSeriesId}.${year}.${field}`}
                year={year}
                month={month}
                isPlanned={isPlanned}
              />
            </Grid2>
          );
        })}
      </Grid2>
    </Grid2>
  );
}

function EditField({
  name,
  disabled = false,
  year,
  month,
  isPlanned = true,
  confirmationType,
}: {
  name: string;
  disabled?: boolean;
  year: string;
  month: number;
  isPlanned?: boolean;
  confirmationType?: UpdateStatus;
}) {
  const {
    getValues,
    setValue,
    register,
    formState: { errors },
  } = useFormContext();
  const values = getValues();
  const value = (getNestedPropValue(values, name) ?? "") as string;
  const [stateValue, setStateValue] = useState(value);

  const error = errors[name];

  const registerReturn = register(name, { disabled });
  const colors = isPlanned
    ? undefined
    : getImpactFieldColors(
        year,
        month,
        value,
        values["startDate"],
        confirmationType
      );

  return (
    <TextField
      value={stateValue}
      {...registerReturn}
      onClick={() => {
        const seriesId = name.split(".")[0].slice(0, name.indexOf("Actual"));

        const actualConfirmation = getValues().actualConfirmation;
        setValue("actualConfirmation", {
          ...actualConfirmation,
          [seriesId]: dayjs().format("YYYY-MM-DD"),
        });
      }}
      onChange={(event) => {
        const value = event.target.value;

        if (value !== "" && !FLOAT_REGEX.test(value)) {
          return;
        }

        setStateValue(value);
        registerReturn.onChange(event);
      }}
      error={!!error}
      sx={{
        display: "flex",
        ".MuiInputBase-root": {
          borderRadius: "5px",
          maxWidth: "75px",
          height: "32px",
          background: disabled ? "#E1E1E1" : "inherit",
          fieldSet: !colors
            ? {}
            : {
                border: colors.border,
                background: colors.background,
              },
        },
      }}
    />
  );
}

function isDateDue(date: Dayjs) {
  const currentDate = dayjs().set("date", 1);
  date.set("date", 1);

  return date.diff(currentDate, "month") <= -2;
}
